<script setup lang="ts">
import FieldError from '@/application/components/ui/FieldError.vue';
import AppButton from '@/application/components/ui/AppButton.vue';
import FaIcon from '@/application/components/ui/FaIcon.vue';
import { useApiWithFormData } from '@/application/composables/apiWithFormData';
import { createBankAccount, getIbanDetails } from '@/api/bankAccount/bankAccountsApi';
import { useDetectMobile } from '@/application/composables/detectMobile';
import { useFormHelper } from '@/application/composables/formHelper';
import type { CreateBankAccount, MinimumBankAccount } from '@/api/bankAccount/types';
import { storeToRefs } from 'pinia';
import { useBaseDataStore } from '@/user/store/baseDataStore';
import { call } from '@/api/lib/integration';
import { ref, watch } from 'vue';
import { useUserDataStore } from '@/user/store/userDataStore';
import SelectContracts from '@/user/components/SelectContracts.vue';
import ContractsField from '@/user/components/ContractsField.vue';
import { TrackEventCategory } from '@/application/utils/tracking/events';
import { useTagManager } from '@demv_systems/feu-tag-manager';
import { ElForm, ElFormItem, ElInput, ElSwitch } from 'element-plus';
import { useRoute, useRouter } from 'vue-router';
import { Finanzmanager } from '@/application/router/types/types.ts';
import { closeHandlung } from '@/api/handlungen/handlungenApi.ts';

const props = defineProps<{ initData?: MinimumBankAccount }>();
const emit = defineEmits(['close', 'updatedAccount']);

const route = useRoute();
const router = useRouter();
const { isMobile } = useDetectMobile();
const { fetchBankAccounts } = useUserDataStore();
const { kundeVorname, kundeNachname, isCompany, firmenname } = storeToRefs(useBaseDataStore());

const ibanDetailsError = ref<string | null>(null);
const showContracts = ref(false);
const showSelectContracts = ref(false);
const ibanDetailsLoading = ref(false);

const ibanFieldHasFocus = ref(false);
const isSubmitting = ref(false);

const { form, clear } = useFormHelper<CreateBankAccount>({
  note: props.initData?.note || null,
  depositor: props.initData?.depositor || isCompany.value ? firmenname.value : `${kundeVorname?.value} ${kundeNachname?.value}`,
  iban: props.initData?.iban || null,
  bic: props.initData?.bic || null,
  bankName: props.initData?.bankName || null,
  main: false,
  contractIds: [],
});

const { submit, validationErrors, resetError, resetErrors } = useApiWithFormData(createBankAccount,
  'Bankverbindung wurde hinzugefügt.',
  () => {
    useTagManager().push({ event: TrackEventCategory.BANK_ACCOUNT, action: 'erstellen' })
    fetchBankAccounts();
    close();
  },
);

async function handleCreateAccount() {
  isSubmitting.value = true;
  showContracts.value = false;
  resetErrors();
  await submit({
    data: {
      depositor: form.depositor ?? '',
      iban: form.iban ?? '',
      bic: form.bic ?? '',
      note: form.note ?? '',
      bankName: form.bankName ?? '',
      main: form.main,
      contractIds: form.contractIds,
    },
    additional: {
      contracts: useUserDataStore().getBankAccountContractByIds(form.contractIds),
    }
  });
  isSubmitting.value = false;

  // if opened from a Handlung
  if (route.name === Finanzmanager.HANDLUNG) {
    await closeHandlung({ handlungId: Number(route.params.id) });
    await router.push({ name: Finanzmanager.BANK_ACCOUNTS });
  }
}

function close() {
  resetErrors();
  ibanDetailsError.value = null;
  showSelectContracts.value = false;
  clear();
  emit('close');
}

async function setIbanDetails() {
  if (!form.iban) {
    form.bic = null;
    form.bankName = null;
    return;
  }

  ibanDetailsLoading.value = true;
  await call(getIbanDetails({ iban: form.iban }),
    (data) => {
      if ('bic' in data) {
        resetErrors();
        form.bic = data.bic;
        form.bankName = data.bankName;
      }
      ibanDetailsLoading.value = false;
    },
    (error) => {
      ibanDetailsError.value = error.message ?? 'Es gab einen Fehler';
      ibanDetailsLoading.value = false;
    },
  );
}

function onIbanFocus() {
  ibanFieldHasFocus.value = true;
  resetError('iban');
  ibanDetailsError.value = null;
}

function onIbanBlur() {
  ibanFieldHasFocus.value = false;
}

watch(ibanFieldHasFocus, (value) => {
  if (value) return;

  setTimeout(
    () => !isSubmitting.value && setIbanDetails(),
    250
  );
})
</script>

<template>
  <div class="flex flex-col">
    <div class="-mx-4 sm:-mx-6 overflow-y-auto pt-3">
      <Transition
        name="fade"
        mode="out-in"
        class="px-4 sm:px-6 py-2"
      >
        <ElForm
          v-if="!showSelectContracts"
          require-asterisk-position="right"
          label-width="auto"
          @submit.prevent
        >
          <div class="flex flex-col gap-4">
            <ElFormItem
              label="Kontoinhaber"
              for="Kontoinhaber"
              :label-position="isMobile ? 'top' : 'left'"
              class="whitespace-nowrap"
              :error="validationErrors.depositor?.[0]"
            >
              <div class="w-full flex justify-end">
                <ElInput
                  id="Kontoinhaber"
                  v-model="form.depositor"
                  placeholder=""
                  class="w-full md:w-80"
                  @focus="resetError('depositor')"
                />
              </div>
              <template #error="{ error }">
                <div class="w-full flex justify-end pt-2">
                  <FieldError class="w-full md:w-80">{{ error }}</FieldError>
                </div>
              </template>
            </ElFormItem>

            <ElFormItem
              label="Bezeichnung"
              for="note"
              :label-position="isMobile ? 'top' : 'left'"
              class="whitespace-nowrap"
              :error="validationErrors.note?.[0]"
            >
              <div class="w-full flex justify-end">
                <ElInput
                  id="note"
                  v-model="form.note"
                  placeholder=""
                  class="w-full md:w-80"
                  @focus="resetError('note')"
                />
              </div>
              <template #error="{ error }">
                <div class="w-full flex justify-end pt-2">
                  <FieldError class="w-full md:w-80">{{ error }}</FieldError>
                </div>
              </template>
            </ElFormItem>

            <ElFormItem
              label="IBAN"
              for="iban"
              :label-position="isMobile ? 'top' : 'left'"
              class="whitespace-nowrap"
              :error="validationErrors.iban?.[0]"
            >
              <div class="w-full flex justify-end">
                <ElInput
                  id="iban"
                  v-model="form.iban"
                  placeholder=""
                  class="w-full md:w-80"
                  @focus="onIbanFocus"
                  @blur="onIbanBlur"
                />
              </div>
              <template #error="{ error }">
                <div class="w-full flex justify-end pt-2">
                  <FieldError class="w-full md:w-80">{{ error }}</FieldError>
                </div>
              </template>
            </ElFormItem>
            <FieldError
              v-if="ibanDetailsError"
              class="-mt-3"
            >
              {{ ibanDetailsError }}
            </FieldError>

            <ElFormItem
              label="BIC"
              for="bic"
              :label-position="isMobile ? 'top' : 'left'"
              class="whitespace-nowrap"
              :error="validationErrors.bic?.[0]"
            >
              <div class="w-full flex justify-end">
                <ElInput
                  id="bic"
                  v-model="form.bic"
                  placeholder=""
                  class="w-full md:w-80"
                  @focus="resetError('bic')"
                >
                  <template v-if="ibanDetailsLoading" #suffix>
                    <FaIcon name="spinner-third" variant="duotone" spin />
                  </template>
                </ElInput>
              </div>
              <template #error="{ error }">
                <div class="w-full flex justify-end pt-2">
                  <FieldError class="w-full md:w-80">{{ error }}</FieldError>
                </div>
              </template>
            </ElFormItem>

            <ElFormItem
              label="Bank"
              for="bank"
              :label-position="isMobile ? 'top' : 'left'"
              class="whitespace-nowrap"
              :error="validationErrors.bankName?.[0]"
            >
              <div class="w-full flex justify-end">
                <ElInput
                  id="bank"
                  v-model="form.bankName"
                  placeholder=""
                  class="w-full md:w-80"
                  @focus="resetError('bankName')"
                >
                  <template v-if="ibanDetailsLoading" #suffix>
                    <FaIcon name="spinner-third" variant="duotone" spin />
                  </template>
                </ElInput>
              </div>
              <template #error="{ error }">
                <div class="w-full flex justify-end pt-2">
                  <FieldError class="w-full md:w-80">{{ error }}</FieldError>
                </div>
              </template>
            </ElFormItem>

            <ElFormItem
              label="Standard"
              :label-position="isMobile ? 'top' : 'left'"
              class="whitespace-nowrap"
            >
              <div class="w-full flex md:justify-end py-3">
                <ElSwitch
                  v-model="form.main"
                  class="md:mr-[272px]"
                  size="large"
                  aria-label="Standardbankverbindung"
                />
              </div>
            </ElFormItem>

            <ContractsField
              :model-value="form.contractIds"
              @show-select-contracts="showSelectContracts = !showSelectContracts"
            />
          </div>
        </ElForm>
        <SelectContracts
          v-else
          v-model="form.contractIds"
          @close="showSelectContracts = false"
        />
      </Transition>
    </div>
    <div class="sticky bottom-0 flex flex-col-reverse sm:flex-row justify-end gap-3 mt-10">
      <AppButton
        type="primary"
        :handler="() => handleCreateAccount()"
      >
        Erstellen
      </AppButton>
    </div>
  </div>
</template>
